'use client'

import { useContext, useState, useEffect } from 'react';
import type { ResourceConfig } from '../interfaces/ResourceConfig';
import { ResourceConfigContext } from '../contexts/ResourceConfigContext';
import { getResource } from '../functions/getResource';

/**
 * @group Hooks
 * 
 * @summary
 * <blockquote>Hook to access ResourceConfig in client components with hydration safety</blockquote>
 * 
 * @remarks
 * This hook is the **recommended way** to access ResourceConfig in client components.
 * It handles hydration automatically by only returning config after the component
 * has mounted on the client, preventing hydration mismatches between server and client.
 * 
 * **Why use this instead of getResource()?**
 * - ✅ Handles hydration automatically (no manual isClient checks needed)
 * - ✅ Returns consistent value during SSR and hydration
 * - ✅ Updates when config changes (via context)
 * - ✅ Works with nested ClientResourceProvider components
 * 
 * **Best Practice:**
 * - In client components: Use `useResourceConfig()` (this hook)
 * - In server components: Use `getResource()` function
 * - In shared code: Use `getResource()` with environment checks
 * 
 * @example
 * ```tsx
 * 'use client'
 * 
 * import { useResourceConfig } from '@shared/config';
 * import { Logger } from '@shared/logger';
 * 
 * function MyComponent() {
 *   const config = useResourceConfig();
 *   
 *   const handleClick = async () => {
 *     Logger.info('Button clicked');
 *   };
 *   
 *   return (
 *     <div>
 *       <p>Transport: {config?.logs?.development?.transport}</p>
 *       <button onClick={handleClick}>Click me</button>
 *     </div>
 *   );
 * }
 * ```
 * 
 * @returns The current ResourceConfig or null if not available yet (during SSR/hydration)
 */
/**
 * @internal
 */
export function useResourceConfig(): ResourceConfig | null {
  const [isClient, setIsClient] = useState(false);
  
  // Wait for client-side hydration to complete
  useEffect(() => {
    setIsClient(true);
  }, []);
  
  // During SSR and hydration, return null to avoid mismatches
  if (!isClient) {
    return null;
  }
  
  // After hydration, get the merged config from the stack
  return getResource();
}

